import { formatAgo, formatDateTime } from "@/lib/format";
import type { SystemStatus } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { releaseText } from "./ReleaseLabel";

type Worker = SystemStatus["workers"][number];

/** For each app the most recent heartbeat is the running one; older ones are from before a deploy. */
export function splitWorkers(workers: Worker[]): { current: Worker[]; previous: Worker[] } {
  const newest = new Map<string, Worker>();
  for (const w of workers) {
    const n = newest.get(w.app);
    if (!n || new Date(w.last_seen_at) > new Date(n.last_seen_at)) newest.set(w.app, w);
  }
  const current = [...newest.values()];
  return { current, previous: workers.filter((w) => !current.includes(w)) };
}

const stale = (w: Worker) => Date.now() - new Date(w.last_seen_at).getTime() >= 120_000;

/** Running workers (red when silent for 2 minutes); heartbeats left from a previous release, muted. */
export function WorkerBadges({ workers, okVariant = "success" }: { workers: Worker[]; okVariant?: "success" | "muted" }) {
  const { current, previous } = splitWorkers(workers);
  if (workers.length === 0) return <Badge variant="warning">No worker heartbeat yet</Badge>;
  return (
    <>
      {current.map((w) => (
        <Badge key={w.worker_id} variant={stale(w) ? "destructive" : okVariant} title={`${releaseText(w.release, w.version)}. Last seen ${formatDateTime(w.last_seen_at)}.`}>
          {w.app} seen {formatAgo(w.last_seen_at)}
        </Badge>
      ))}
      {previous.map((w) => (
        <Badge key={w.worker_id} variant="muted" title={`${releaseText(w.release, w.version)}: stopped when a newer release started. Last seen ${formatDateTime(w.last_seen_at)}.`}>
          {w.app}, previous release, last seen {formatAgo(w.last_seen_at)}
        </Badge>
      ))}
    </>
  );
}
